import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Download, RefreshCw, Check, Ban, Pencil, X, Search, ShieldCheck,
  AlertTriangle, Globe, CircleSlash, Save, Loader2, Upload, Wand2,
} from "lucide-react";
import api from "../lib/api";
import { CurioImportModal } from "../components/CurioImportModal";
import { BulkImagesButton, VerifySourcesButton } from "../components/WorkbenchJobs";
import { LegacyUpgradePanel } from "../components/LegacyUpgradePanel";
import { ExpandPanel } from "../components/ExpandPanel";
import { Layout } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const SUBJECTS = ["human-body-weirdness", "strange-history", "unusual-animals", "the-universe",
  "earth", "nature", "eccentric-aristocrats", "science", "forgotten-inventions", "entertainment"];
const STATUSES = ["all", "published", "pending", "evidencing", "legacy", "needs_review", "filler"];

const STATUS_STYLE = {
  published: "text-[#2F6B33] bg-[#EAF3EA] border-[#BFD9BF]",
  pending: "text-[#8A5A00] bg-[#FBF1DD] border-[#E4D3A8]",
  evidencing: "text-[#7A4B00] bg-[#FBEAD2] border-[#E4C38A]",
  legacy: "text-[#9A5B1E] bg-[#FBEBDD] border-[#E6C2A0]",
  needs_review: "text-[#8A5A00] bg-[#FBF1DD] border-[#E4D3A8]",
  filler: "text-[#8A1C1C] bg-[#F7E3E3] border-[#E0B3B3]",
};

const BADGE = {
  wikipedia: { cls: "text-[#2F6B33] bg-[#EAF3EA] border-[#BFD9BF]", icon: ShieldCheck, label: "Wikipedia" },
  allowlisted: { cls: "text-[#8A5A00] bg-[#FBF1DD] border-[#E4D3A8]", icon: AlertTriangle, label: "Allowlisted" },
  legacy: { cls: "text-[#9A5B1E] bg-[#FBEBDD] border-[#E6C2A0]", icon: Globe, label: "Legacy" },
  other: { cls: "text-[#4A4036] bg-[#F3EDE0] border-[#D8CEB9]", icon: Globe, label: "Other" },
  missing: { cls: "text-[#8A1C1C] bg-[#F7E3E3] border-[#E0B3B3]", icon: CircleSlash, label: "Missing" },
};

const StatCard = ({ label, value, testid }) => (
  <div data-testid={testid} className="bg-white border border-[#D8CEB9] rounded-sm px-4 py-3 min-w-[110px]">
    <p className="font-mono-cv text-[10px] uppercase tracking-wider text-[#7A6C5D]">{label}</p>
    <p className="font-serif-display text-2xl font-bold text-[#1A1615]">{value}</p>
  </div>
);

const SrcBadges = ({ badges }) => (
  <div className="flex flex-wrap gap-1">
    {(badges || []).map((b, i) => {
      const d = BADGE[b.kind] || BADGE.other; const I = d.icon;
      return (
        <span key={i} title={b.url} className={`inline-flex items-center gap-1 text-[10px] font-medium border rounded-full px-1.5 py-0.5 ${d.cls}`}>
          <I className="w-3 h-3" /> {d.label}
        </span>
      );
    })}
  </div>
);

export default function AdminCuriosWorkbench() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [subject, setSubject] = useState("");
  const [source, setSource] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [edit, setEdit] = useState(null);
  const [importOpen, setImportOpen] = useState(() => new URLSearchParams(window.location.search).get("import") === "1");
  const [dirty, setDirty] = useState(false);
  const [regen, setRegen] = useState({});
  const [showExpand, setShowExpand] = useState(false);

  useEffect(() => { if (ready && !user) nav("/admin", { replace: true }); }, [ready, user, nav]);

  const loadStats = useCallback(() => {
    api.get("/admin/curios/workbench/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/curios/workbench/list", { params: { status, subject, source, q } })
      .then((r) => { setRows(r.data.rows); setSel([]); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, [status, subject, source, q]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggleSel = (slug) => setSel((s) => s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]);
  const allSel = rows.length > 0 && sel.length === rows.length;

  const bulk = async (action) => {
    if (!sel.length) return;
    if (action === "quarantine" && !window.confirm(`Quarantine ${sel.length} Curio(s)?`)) return;
    try {
      const r = await api.post("/admin/curios/workbench/bulk", { slugs: sel, action });
      toast.success(`${action === "approve" ? "Approved" : "Quarantined"} ${r.data.updated}. ${r.data.ready_for_redeploy ? "Ready for redeploy." : ""}`);
      if (r.data.ready_for_redeploy) setDirty(true);
      load(); loadStats();
    } catch { toast.error("Bulk action failed"); }
  };

  const rowAction = async (slug, action) => {
    try {
      await api.post("/admin/curios/workbench/bulk", { slugs: [slug], action });
      toast.success(action === "approve" ? "Approved — ready for redeploy" : "Quarantined");
      if (action === "approve") setDirty(true);
      load(); loadStats();
    } catch { toast.error("Action failed"); }
  };

  const quickRegen = async (slug) => {
    setRegen((m) => ({ ...m, [slug]: true }));
    try {
      const r = await api.post("/admin/curios/workbench/regenerate-image", { slug, save: true });
      setRows((rs) => rs.map((x) => (x.slug === slug ? { ...x, image_url: r.data.image_url } : x)));
      setDirty(true);
      toast.success("New sepia engraving saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Image generation failed"); }
    finally { setRegen((m) => ({ ...m, [slug]: false })); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/curios/export", { params: { status: "all" }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `curiovault_curios_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Fresh CSV exported");
    } catch { toast.error("Export failed"); }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="curio-workbench">
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div>
            <h1 className="font-serif-display text-3xl font-bold text-[#1A1615]">Curio Workbench</h1>
            <p className="font-ui text-sm text-[#7A6C5D]">See, edit and approve every Curio — including pending & legacy states.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/curios" className="font-ui text-sm text-[#9E4535] hover:underline">← Manage Curios</Link>
            <button data-testid="wb-import-btn" onClick={() => setImportOpen(true)} className="inline-flex items-center gap-1.5 border border-[#9E4535] text-[#9E4535] font-ui text-sm font-semibold px-3 py-2 rounded-sm hover:bg-[#F3EDE0]">
              <Upload className="w-4 h-4" /> Import CSV
            </button>
            <button data-testid="wb-export-btn" onClick={exportCsv} className="inline-flex items-center gap-1.5 bg-[#9E4535] text-[#FAF7F0] font-ui text-sm font-semibold px-3 py-2 rounded-sm hover:bg-[#82382B]">
              <Download className="w-4 h-4" /> Export fresh CSV
            </button>
            <button data-testid="wb-redeploy-btn" onClick={() => toast("Use the Deploy button at the top of the chat to publish to curiovault.co", { icon: "🚀" })} className="inline-flex items-center gap-1.5 border border-[#C5A059] text-[#9E4535] font-ui text-sm font-semibold px-3 py-2 rounded-sm hover:bg-[#F3EDE0]">
              <RefreshCw className="w-4 h-4" /> Redeploy
            </button>
          </div>
        </div>

        {dirty && (
          <div data-testid="wb-ready-redeploy" className="mb-4 flex items-center gap-2 bg-[#FBF1DD] border border-[#E4D3A8] text-[#8A5A00] font-ui text-sm px-3 py-2 rounded-sm">
            <RefreshCw className="w-4 h-4" /> Ready for redeploy: changes saved to this database. Redeploy to push code; data lives per-environment.
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="flex flex-wrap gap-3 mb-6" data-testid="wb-stats">
            <StatCard label="Total in DB" value={stats.total} testid="stat-total" />
            <StatCard label="Published" value={stats.published} testid="stat-published" />
            <StatCard label="Pending" value={stats.pending + stats.needs_review} testid="stat-pending" />
            <StatCard label="Evidencing" value={stats.evidencing} testid="stat-evidencing" />
            <StatCard label="Legacy source" value={stats.legacy} testid="stat-legacy" />
            <StatCard label="Filler" value={stats.filler} testid="stat-filler" />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="wb-tools">
          <BulkImagesButton onDone={() => { setDirty(true); load(); }} />
          <VerifySourcesButton onDone={() => { load(); loadStats(); }} />
          <button data-testid="wb-expand-toggle" onClick={() => setShowExpand((v) => !v)} className={`inline-flex items-center gap-1.5 border font-ui text-xs font-semibold px-3 py-1.5 rounded-sm ${showExpand ? "border-[#9E4535] text-[#9E4535] bg-[#F3EDE0]" : "border-[#D8CEB9] bg-white text-[#4A4036] hover:border-[#9E4535]"}`}>
            Longer articles
          </button>
        </div>

        {showExpand && <ExpandPanel onChange={() => { setDirty(true); load(); }} />}

        {status === "legacy" && <LegacyUpgradePanel rows={rows} onChange={(changed) => { if (changed) setDirty(true); load(); loadStats(); }} />}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="flex flex-wrap gap-1" data-testid="wb-status-tabs">
            {STATUSES.map((s) => (
              <button key={s} data-testid={`wb-tab-${s}`} onClick={() => setStatus(s)}
                className={`font-ui text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${status === s ? "bg-[#1A1615] text-[#FAF7F0] border-[#1A1615]" : "bg-white text-[#4A4036] border-[#D8CEB9] hover:bg-[#F3EDE0]"}`}>
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
          <select data-testid="wb-subject-filter" value={subject} onChange={(e) => setSubject(e.target.value)} className="font-ui text-sm border border-[#D8CEB9] rounded-sm px-2 py-1.5 bg-white">
            <option value="">All subjects</option>
            {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="wb-source-filter" value={source} onChange={(e) => setSource(e.target.value)} className="font-ui text-sm border border-[#D8CEB9] rounded-sm px-2 py-1.5 bg-white">
            <option value="">Any source</option>
            <option value="wikipedia">Has Wikipedia</option>
            <option value="allowlisted">Has allowlisted</option>
            <option value="legacy">Has google legacy</option>
            <option value="missing">Missing source</option>
          </select>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#9A8C7D]" />
            <input data-testid="wb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search slug/title/hook" className="font-ui text-sm border border-[#D8CEB9] rounded-sm pl-7 pr-2 py-1.5 bg-white w-56" />
          </div>
        </div>

        {/* Bulk bar */}
        {sel.length > 0 && (
          <div className="flex items-center gap-3 mb-3 bg-[#F3EDE0] border border-[#D8CEB9] rounded-sm px-3 py-2" data-testid="wb-bulk-bar">
            <span className="font-ui text-sm text-[#4A4036]">{sel.length} selected</span>
            <button data-testid="wb-bulk-approve" onClick={() => bulk("approve")} className="inline-flex items-center gap-1 text-sm font-semibold text-[#2F6B33]"><Check className="w-4 h-4" /> Approve</button>
            <button data-testid="wb-bulk-quarantine" onClick={() => bulk("quarantine")} className="inline-flex items-center gap-1 text-sm font-semibold text-[#8A1C1C]"><Ban className="w-4 h-4" /> Quarantine</button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-[#D8CEB9] rounded-sm overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-[#F3EDE0] border-b border-[#D8CEB9]">
              <tr className="font-mono-cv text-[10px] uppercase tracking-wider text-[#7A6C5D]">
                <th className="p-2"><input type="checkbox" data-testid="wb-select-all" checked={allSel} onChange={() => setSel(allSel ? [] : rows.map((r) => r.slug))} /></th>
                <th className="p-2">Img</th><th className="p-2">Slug / Title</th><th className="p-2">Subject</th>
                <th className="p-2">Sources</th><th className="p-2">Status</th><th className="p-2">Len</th><th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="p-8 text-center text-[#9A8C7D]"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="8" className="p-8 text-center font-ui text-[#9A8C7D]">No Curios match.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.slug} data-testid={`wb-row-${r.slug}`} className="border-b border-[#EDE6D6] hover:bg-[#FBF8F0]">
                  <td className="p-2"><input type="checkbox" data-testid={`wb-check-${r.slug}`} checked={sel.includes(r.slug)} onChange={() => toggleSel(r.slug)} /></td>
                  <td className="p-2">
                    <div className="flex items-center gap-1.5">
                      {r.image_url ? <img src={r.image_url} alt="" className="w-12 h-9 object-cover rounded-sm border border-[#D8CEB9]" /> : <div className="w-12 h-9 bg-[#F3EDE0] rounded-sm" />}
                      <button title="Regenerate sepia engraving" onClick={() => quickRegen(r.slug)} disabled={regen[r.slug]} data-testid={`wb-regen-${r.slug}`} className="text-[#9A8C7D] hover:text-[#9E4535] disabled:opacity-50">
                        {regen[r.slug] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </td>
                  <td className="p-2 max-w-[260px]">
                    <button onClick={() => setEdit(r.slug)} data-testid={`wb-open-${r.slug}`} className="font-ui text-sm font-semibold text-[#9E4535] hover:underline text-left block truncate">{r.title}</button>
                    <span className="font-mono-cv text-[10px] text-[#9A8C7D] truncate block">{r.slug}</span>
                  </td>
                  <td className="p-2 font-ui text-xs text-[#4A4036]">{r.subject_slug}</td>
                  <td className="p-2"><SrcBadges badges={r.badges} /></td>
                  <td className="p-2"><span className={`inline-block text-[10px] font-semibold border rounded-full px-2 py-0.5 ${STATUS_STYLE[r.wb_status] || ""}`}>{r.wb_status.replace("_", " ")}</span></td>
                  <td className="p-2 font-ui text-xs text-[#4A4036]">{r.fact_len}</td>
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <button title="Edit" onClick={() => setEdit(r.slug)} data-testid={`wb-edit-${r.slug}`} className="text-[#4A4036] hover:text-[#9E4535]"><Pencil className="w-4 h-4" /></button>
                      <button title="Approve" onClick={() => rowAction(r.slug, "approve")} data-testid={`wb-approve-${r.slug}`} className="text-[#2F6B33]"><Check className="w-4 h-4" /></button>
                      <button title="Quarantine" onClick={() => rowAction(r.slug, "quarantine")} data-testid={`wb-quarantine-${r.slug}`} className="text-[#8A1C1C]"><Ban className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="font-ui text-xs text-[#9A8C7D] mt-2">{rows.length} shown</p>
      </div>

      {edit && <EditDrawer slug={edit} onClose={() => setEdit(null)} onSaved={() => { setDirty(true); load(); loadStats(); }} />}
      {importOpen && <CurioImportModal onClose={() => setImportOpen(false)} onDone={(d) => { if (d.ready_for_redeploy) setDirty(true); load(); loadStats(); }} />}
    </Layout>
  );
}

function EditDrawer({ slug, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checks, setChecks] = useState({});
  const [genBusy, setGenBusy] = useState(false);
  const [genStatus, setGenStatus] = useState("");

  const regenerate = async () => {
    setGenBusy(true); setGenStatus("Generating… (~15s)");
    try {
      const r = await api.post("/admin/curios/workbench/regenerate-image", { slug, title: d.title, hook: d.hook });
      setD((p) => ({ ...p, image_url: r.data.image_url }));
      setGenStatus("New image ready — click Save to keep it");
    } catch (e) { setGenStatus(e?.response?.data?.detail || "Generation failed"); }
    finally { setGenBusy(false); }
  };

  useEffect(() => {
    api.get(`/admin/curios/workbench/item/${slug}`).then((r) => setD(r.data)).catch(() => toast.error("Load failed"));
  }, [slug]);

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const setSrc = (i, v) => setD((p) => { const s = [...(p.sources || [])]; s[i] = { ...(s[i] || {}), url: v }; return { ...p, sources: s }; });

  const checkUrl = async (i, url) => {
    if (!url) return;
    setChecks((c) => ({ ...c, [i]: { kind: "loading" } }));
    try {
      const r = await api.get("/admin/curios/workbench/check-url", { params: { url } });
      setChecks((c) => ({ ...c, [i]: r.data }));
    } catch { setChecks((c) => ({ ...c, [i]: { kind: "fail", label: "error" } })); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/curios/workbench/item/${slug}`, {
        title: d.title, hook: d.hook, full_fact: d.full_fact, subject_slug: d.subject_slug,
        tags: d.tags, image_url: d.image_url, image_alt: d.image_alt, social_caption: d.social_caption,
        sources: d.sources, is_filler: d.is_filler, needs_review: d.needs_review,
        legacy_source_needs_upgrade: d.legacy_source_needs_upgrade, published: d.published,
      });
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const CKCOL = { ok: "text-[#2F6B33]", warning: "text-[#8A5A00]", fail: "text-[#8A1C1C]", loading: "text-[#9A8C7D]" };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="wb-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-4xl bg-[#FAF7F0] h-full overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#D8CEB9] sticky top-0 bg-[#FAF7F0] z-10">
          <h2 className="font-serif-display text-xl font-bold text-[#1A1615]">Edit Curio</h2>
          <div className="flex items-center gap-2">
            <button data-testid="wb-save-btn" onClick={save} disabled={saving || !d} className="inline-flex items-center gap-1.5 bg-[#9E4535] text-[#FAF7F0] font-ui text-sm font-semibold px-4 py-2 rounded-sm disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
            <button onClick={onClose} className="text-[#4A4036]"><X className="w-5 h-5" /></button>
          </div>
        </div>
        {!d ? <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#9A8C7D]" /></div> : (
          <div className="grid lg:grid-cols-2 gap-6 p-5">
            {/* Preview */}
            <div>
              <p className="font-mono-cv text-[10px] uppercase tracking-wider text-[#7A6C5D] mb-2">Live preview</p>
              <div className="bg-white border border-[#D8CEB9] rounded-sm overflow-hidden">
                {d.image_url && <img src={d.image_url} alt={d.image_alt} className="w-full aspect-[3/2] object-cover" />}
                <div className="p-4">
                  <span className="font-mono-cv text-[10px] uppercase tracking-wider text-[#9E4535]">{d.subject_slug}</span>
                  <h3 className="font-serif-display text-xl font-bold text-[#1A1615] mt-1">{d.title}</h3>
                  <p className="font-ui text-sm text-[#7A6C5D] italic mt-1">{d.hook}</p>
                  <p className="font-body text-sm text-[#2E282A] leading-relaxed mt-3">{d.full_fact}</p>
                </div>
              </div>
              {/* Evidencing */}
              <div className="mt-4 bg-white border border-[#D8CEB9] rounded-sm p-3">
                <p className="font-mono-cv text-[10px] uppercase tracking-wider text-[#7A6C5D] mb-2">Evidencing</p>
                <p className="font-ui text-xs text-[#4A4036]">Credibility badge: <b>{d.credibility_badge ? "Fact-checked" : "—"}</b></p>
                <p className="font-ui text-xs text-[#4A4036] mt-1">Duplicate-slug matches: {(d.duplicate_slugs || []).length ? d.duplicate_slugs.join(", ") : "none"}</p>
              </div>
            </div>
            {/* Form */}
            <div className="space-y-3">
              {[["title", "Title"], ["hook", "Hook"], ["image_alt", "Image alt"], ["social_caption", "Social caption"]].map(([k, l]) => (
                <label key={k} className="block">
                  <span className="font-ui text-xs font-semibold text-[#4A4036]">{l}</span>
                  <input data-testid={`wb-field-${k}`} value={d[k] || ""} onChange={(e) => set(k, e.target.value)} className="w-full border border-[#D8CEB9] rounded-sm px-2 py-1.5 font-ui text-sm bg-white" />
                </label>
              ))}
              <label className="block">
                <span className="font-ui text-xs font-semibold text-[#4A4036]">Full fact <span className="text-[#9A8C7D]">({(d.full_fact || "").length} chars{(d.full_fact || "").length < 200 ? " — short" : ""})</span></span>
                <textarea data-testid="wb-field-full_fact" value={d.full_fact || ""} onChange={(e) => set("full_fact", e.target.value)} rows={6} className="w-full border border-[#D8CEB9] rounded-sm px-2 py-1.5 font-body text-sm bg-white" />
              </label>
              <label className="block">
                <span className="font-ui text-xs font-semibold text-[#4A4036]">Subject</span>
                <select data-testid="wb-field-subject" value={d.subject_slug || ""} onChange={(e) => set("subject_slug", e.target.value)} className="w-full border border-[#D8CEB9] rounded-sm px-2 py-1.5 font-ui text-sm bg-white">
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="font-ui text-xs font-semibold text-[#4A4036]">Tags (comma-separated)</span>
                <input data-testid="wb-field-tags" value={(d.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} className="w-full border border-[#D8CEB9] rounded-sm px-2 py-1.5 font-ui text-sm bg-white" />
              </label>
              <label className="block">
                <span className="font-ui text-xs font-semibold text-[#4A4036]">Image URL</span>
                <div className="flex items-center gap-2">
                  {d.image_url ? <img src={d.image_url} alt="" data-testid="wb-image-thumb" className="w-12 h-9 object-cover rounded-sm border border-[#D8CEB9] shrink-0" /> : <div className="w-12 h-9 bg-[#F3EDE0] rounded-sm shrink-0" />}
                  <input data-testid="wb-field-image_url" value={d.image_url || ""} onChange={(e) => set("image_url", e.target.value)} className="flex-1 min-w-0 border border-[#D8CEB9] rounded-sm px-2 py-1.5 font-ui text-xs bg-white" />
                </div>
              </label>
              <div className="flex items-center gap-2 -mt-1">
                <button type="button" onClick={regenerate} disabled={genBusy} data-testid="wb-regenerate-image-btn" className="inline-flex items-center gap-1.5 border border-[#C5A059] text-[#9E4535] font-ui text-xs font-semibold px-3 py-1.5 rounded-sm hover:bg-[#F3EDE0] disabled:opacity-50">
                  {genBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Regenerate sepia engraving
                </button>
                {genStatus && <span data-testid="wb-regenerate-status" className="font-ui text-[11px] text-[#7A6C5D]">{genStatus}</span>}
              </div>
              {/* Sources with HEAD check */}
              <div>
                <span className="font-ui text-xs font-semibold text-[#4A4036]">Sources</span>
                {[0, 1].map((i) => (
                  <div key={i} className="flex items-center gap-2 mt-1">
                    <input data-testid={`wb-src-${i}`} value={(d.sources?.[i]?.url) || ""} onChange={(e) => setSrc(i, e.target.value)} placeholder={i === 0 ? "src1 (Wikipedia preferred)" : "src2 (allowlisted secondary)"} className="flex-1 border border-[#D8CEB9] rounded-sm px-2 py-1.5 font-ui text-xs bg-white" />
                    <button data-testid={`wb-check-src-${i}`} onClick={() => checkUrl(i, d.sources?.[i]?.url)} className="text-xs border border-[#D8CEB9] rounded-sm px-2 py-1.5 hover:bg-[#F3EDE0] whitespace-nowrap">Check</button>
                    {checks[i] && <span className={`text-xs font-semibold ${CKCOL[checks[i].kind] || ""}`}>{checks[i].kind === "loading" ? "…" : checks[i].label}</span>}
                  </div>
                ))}
              </div>
              {/* Toggles */}
              <div className="flex flex-wrap gap-4 pt-2 border-t border-[#D8CEB9]">
                {[["published", "Published"], ["needs_review", "Needs review"], ["legacy_source_needs_upgrade", "Legacy source"], ["is_filler", "Filler"]].map(([k, l]) => (
                  <label key={k} className="inline-flex items-center gap-1.5 font-ui text-xs text-[#4A4036]">
                    <input type="checkbox" data-testid={`wb-toggle-${k}`} checked={!!d[k]} onChange={(e) => set(k, e.target.checked)} /> {l}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
